/**
 * @fileoverview Human-readable formatter for audit log change values.
 * @module ui-admin/utils/formatAuditValue
 *
 * Responsibilities:
 * - Convert raw audit change values (objects, arrays, dates, primitives)
 *   into short, human-readable display strings for the audit timeline UI.
 * - Provide a compact summary for complex/nested data without rendering
 *   full JSON in the DOM — critical for mobile PWA memory and layout
 *   performance on limited-network / offline fieldwork devices.
 *
 * Dependencies:
 * - None (pure function, zero external dependencies).
 *
 * Architecture:
 * - Shared utility consumed by AuditLogTimeline, AuditLogDetailPanel, and
 *   expandAuditChanges. Replaces per-component `formatValue` stubs that
 *   previously dumped raw JSON.stringify output into the DOM.
 *
 * Notes:
 * - Timeline / compact cells cap at MAX_DISPLAY_LENGTH.
 * - Audit Log Detail Field Changes uses formatAuditValueFull so long
 *   Before/After text is not cut off.
 * - ISO date detection uses a lightweight regex — no Date parsing unless
 *   the pattern matches, keeping hot-path cost near zero.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Maximum characters rendered in a single value cell. */
const MAX_DISPLAY_LENGTH = 120;

/**
 * Lightweight ISO-8601 date pattern.
 * Matches strings like "2025-06-01T00:00:00.000Z" or "2025-06-01".
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:\d{2})?)?$/;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * @process formatAuditValue
 *
 * Steps:
 * 1. Handle null / undefined → "(empty)".
 * 2. Handle booleans → "Yes" / "No".
 * 3. Handle strings — detect ISO dates and format them; truncate long text.
 * 4. Handle numbers → locale string.
 * 5. Handle arrays → count + preview of first items.
 * 6. Handle objects → extract readable label fields, fall back to key summary.
 *
 * Inputs:
 * - val: any — the raw audit ChangeDetail value (oldValue or newValue).
 *
 * Outputs:
 * - A short, human-readable string safe for inline DOM rendering.
 *
 * Failure Handling:
 * - Never throws; falls back to "(unknown)" on unexpected input.
 *
 * Idempotency:
 * - REQUIRED — pure function, no side effects.
 */
export function formatAuditValue(val: unknown): string {
  return formatAuditValueInternal(val, MAX_DISPLAY_LENGTH);
}

/**
 * @process formatAuditValueFull
 *
 * Steps:
 * 1. Same type handling as formatAuditValue.
 * 2. Do not truncate strings so Field Changes shows the full before/after.
 *
 * Inputs:
 * - val: raw audit ChangeDetail value.
 *
 * Outputs:
 * - Human-readable string with no length cap.
 *
 * Failure Handling:
 * - Never throws.
 *
 * Idempotency: REQUIRED.
 */
export function formatAuditValueFull(val: unknown): string {
  return formatAuditValueInternal(val, Number.POSITIVE_INFINITY);
}

function formatAuditValueInternal(val: unknown, maxLength: number): string {
  // Null / undefined
  if (val === null || val === undefined) return '(empty)';

  // Boolean
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';

  // Number
  if (typeof val === 'number') return String(val);

  // String — detect ISO dates, truncate long text
  if (typeof val === 'string') {
    if (val.length === 0) return '(empty)';
    if (ISO_DATE_RE.test(val)) return formatISODate(val);
    return truncate(val, maxLength);
  }

  // Array
  if (Array.isArray(val)) {
    return formatArray(val, maxLength);
  }

  // Object
  if (typeof val === 'object') {
    return formatObject(val as Record<string, unknown>, maxLength);
  }

  return String(val);
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Format an ISO date string into a concise, locale-friendly display.
 * Uses compact month/day/year format without seconds for readability.
 */
function formatISODate(iso: string): string {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    // If time is midnight (date-only), show just the date
    if (iso.length <= 10 || (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Format an array into a readable summary.
 * - Empty array → "(none)"
 * - Simple primitive array → comma-separated preview
 * - Object array → count + label preview if available
 */
function formatArray(arr: unknown[], maxLength: number = MAX_DISPLAY_LENGTH): string {
  if (arr.length === 0) return '(none)';

  // Check if all items are simple primitives
  const allPrimitive = arr.every(
    (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean',
  );

  if (allPrimitive) {
    const joined = arr.map(String).join(', ');
    return truncate(joined, maxLength);
  }

  // Object array — try to extract readable labels
  const labels = arr
    .slice(0, 3)
    .map((item) => extractLabel(item))
    .filter(Boolean);

  if (labels.length > 0) {
    const suffix = arr.length > 3 ? ` (+${arr.length - 3} more)` : '';
    return truncate(labels.join(', ') + suffix, maxLength);
  }

  // Fallback: just show count
  return `${arr.length} item${arr.length === 1 ? '' : 's'}`;
}

/**
 * Format an object into a readable summary.
 * Priority:
 * 1. Extract a recognisable "label" field (name, label, title, code, email).
 * 2. For address-like objects, format as a single-line address.
 * 3. Fall back to a key-count summary with top-level key names.
 */
export function isInspectionResponse(obj: Record<string, unknown>): boolean {
  if (!('value' in obj) && !('isNA' in obj)) return false;
  if (typeof obj.questionText === 'string' || typeof obj.fieldType === 'string' || 'isNA' in obj) {
    return true;
  }
  if ('name' in obj || 'displayName' in obj || 'email' in obj) {
    return false;
  }
  return true;
}

function formatInspectionResponse(obj: Record<string, unknown>, maxLength: number = MAX_DISPLAY_LENGTH): string {
  if (obj.isNA === true) return 'N/A';
  const value = obj.value;
  if (value === null || value === undefined || value === '') return '(empty)';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return truncate(value, maxLength);
  if (Array.isArray(value)) return formatArray(value, maxLength);
  return truncate(String(value), maxLength);
}

function formatObject(obj: Record<string, unknown>, maxLength: number = MAX_DISPLAY_LENGTH): string {
  if (isInspectionResponse(obj)) return formatInspectionResponse(obj, maxLength);

  // Recognisable label fields (ordered by preference)
  const label = extractLabel(obj);
  if (label) return truncate(label, maxLength);

  // Address-like object
  if ('street1' in obj || 'city' in obj || 'state' in obj) {
    return formatAddress(obj, maxLength);
  }

  // LookupReference-like: { _id, name, code }
  if ('_id' in obj && typeof obj._id === 'string') {
    const parts: string[] = [];
    if (typeof obj.name === 'string') parts.push(obj.name);
    if (typeof obj.code === 'string') parts.push(`(${obj.code})`);
    if (parts.length > 0) return truncate(parts.join(' '), maxLength);
  }

  // Fallback: key-count summary
  const keys = Object.keys(obj).filter((k) => !k.startsWith('_'));
  if (keys.length === 0) return '(empty object)';
  if (keys.length <= 3) {
    const preview = keys.map((k) => `${k}: ${summariseScalar(obj[k])}`).join(', ');
    return truncate(preview, maxLength);
  }
  return `${keys.length} fields (${keys.slice(0, 3).join(', ')}…)`;
}

/**
 * Extract a human-readable label from an object.
 * Checks common identifier fields in priority order.
 */
function extractLabel(item: unknown): string {
  if (!item || typeof item !== 'object') return '';
  const obj = item as Record<string, unknown>;
  if (isInspectionResponse(obj)) return '';
  for (const key of ['name', 'label', 'title', 'displayName', 'code', 'email']) {
    const v = obj[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return '';
}

/**
 * Format an address-like object into a one-line string.
 */
function formatAddress(obj: Record<string, unknown>, maxLength: number = MAX_DISPLAY_LENGTH): string {
  const parts: string[] = [];
  if (typeof obj.street1 === 'string' && obj.street1) parts.push(obj.street1);
  if (typeof obj.street2 === 'string' && obj.street2) parts.push(obj.street2);
  if (typeof obj.city === 'string' && obj.city) parts.push(obj.city);
  if (typeof obj.state === 'string' && obj.state) parts.push(obj.state);
  if (typeof obj.zip === 'string' && obj.zip) parts.push(obj.zip);
  if (typeof obj.zipCode === 'string' && obj.zipCode) parts.push(obj.zipCode);
  return parts.length > 0 ? truncate(parts.join(', '), maxLength) : '(empty address)';
}

/**
 * Ultra-compact one-word summary of a scalar for key:value previews.
 */
function summariseScalar(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? 'Yes' : 'No';
  if (typeof val === 'number') return String(val);
  if (typeof val === 'string') {
    if (val.length === 0) return '—';
    if (ISO_DATE_RE.test(val)) return formatISODate(val);
    return val.length > 30 ? val.slice(0, 27) + '…' : val;
  }
  if (Array.isArray(val)) return `${val.length} item${val.length === 1 ? '' : 's'}`;
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    if (isInspectionResponse(obj)) return formatInspectionResponse(obj);
    const nestedLabel = extractLabel(obj);
    if (nestedLabel) return nestedLabel.length > 30 ? nestedLabel.slice(0, 27) + '…' : nestedLabel;
    return 'object';
  }
  return String(val);
}

/**
 * Truncate a string to a maximum length, appending an ellipsis if cut.
 */
function truncate(str: string, max: number): string {
  if (!Number.isFinite(max) || str.length <= max) return str;
  return str.slice(0, max - 1) + '…';
}
